<template>
  <div v-if="!current && !layouts.editing" class="page-container">
    <h2 class="page-title">Page Not Found</h2>

    <p>This page could not be found. Please check your URL and try again.</p>
    <Button v-if="user.isAdmin" @click="layouts.editing = true">
      <i class="pi pi-unlock" /> Create {{ route.path }}
    </Button>
  </div>
  <main
    v-else
    :class="{ page: true, [`page-${current?.name || 'unknown'}`]: true }"
  >
    <div v-if="user.isAdmin && layouts.editing === true" class="flex-column">
      <div v-if="error">
        <Message severity="error">{{ error }}</Message>
      </div>
      <div class="flex-row align-center gap-4 url-line">
        <div class="flex-row gap-2 align-center">
          <label class="text-right">Title:</label>
          <InputText
            v-model="title"
            @change="saveLayoutTitle(title)"
            :invalid="!!error"
            :aria-errormessage="error"
          />
        </div>
        <div class="flex-row gap-2 align-center">
          <label class="text-right">URL:</label>
          <InputText
            v-model="url"
            @change="saveLayoutURL(url)"
            :invalid="!!error"
            :aria-errormessage="error"
          />
        </div>
      </div>
    </div>
    <LayoutConnector :name="route.path" :manifest="manifest" :form="AutoForm" />
  </main>
</template>

<script setup lang="ts">
import AutoForm from "@/components/PapaAutoForm.vue";
import { useUserStore } from "@/stores/user";
import { manifest } from "@/utils/layout_manifest";
import { useHead } from "@unhead/vue";
import copy from "@virgodev/bazaar/functions/copy";
import LayoutConnector from "@virgodev/web-builder/components/LayoutConnector.vue";
import { useLayoutStore } from "@virgodev/web-builder/stores/layouts";
import type { LayoutCard } from "@virgodev/web-builder/utils/layout";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Message from "primevue/message";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const user = useUserStore();
const layouts = useLayoutStore();
const route = useRoute();
const router = useRouter();

const title = ref("Not Found");
const url = ref(route.path);
const error = ref("");

const current = computed(() => {
  return layouts.list.find((layout) => layout.name === route.path);
});

watch(current, () => {
  if (current.value) {
    url.value = current.value.name;
    title.value = current.value.options.title || "Page";
  }
});

useHead({
  title: title,
});

function saveLayoutTitle(v: string) {
  if (v && current.value && v !== current.value.options.title) {
    const clone = copy(current.value) as LayoutCard;
    clone.options.title = v;
    layouts.save(clone);
  }
}

function saveLayoutURL(v: string) {
  if (v && current.value && v !== current.value.name) {
    // make sure no other has the same name
    if (v === "/notices") {
      error.value = "The URL cannot be changed to /notices";
    } else if (!/\/page/.test(v) && !/\/notice/.test(v)) {
      error.value = "The URL must start with /page or /notice";
    } else if (
      layouts.list.some(
        (layout) => layout.id !== current.value?.id && layout.name === v,
      )
    ) {
      error.value = "A layout with the same URL already exists";
    } else {
      const clone = copy(current.value) as LayoutCard;
      clone.name = v;
      layouts.save(clone);
      router.replace(v);
    }
  }
}
</script>

<style scoped>
.page-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 64px;
}
.page-title {
  text-align: center;
  color: #ec008c;

  font-size: 32px;
  font-weight: 500;
}
</style>
